import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Button,
  Chip,
  IconButton,
  TableContainer,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { AddCircleOutline, Delete } from "@mui/icons-material";
import moment from "moment";
import {
  MonthlyHolidayData,
  MonthlyTourProgramData,
} from "../../../../../domain/models/monthly-tour-program/fetch-monthly-tour-program";
import AddTourProgramFormModal from "../Modal/AddTourProgramFormModal";
import SmartFillModal from "../Modal/SmartFillModal"; // Assuming you have this modal
import { MonthlyTourProgramStore } from "../../../../../store/main/MonthlyTourProgramStore";
import ConfirmDeleteModal from "../Modal/ConfirmDeleteModal";
import { CustomerDatabaseStore } from "../../../../../store/main/CustomerDatabaseStore";
import {
  MonthlySmartFillData,
  MonthlySmartFillHolidayData,
} from "../../../../../domain/models/monthly-tour-program/fetch-monthly-smart-fill";
import IMAGES from "../../../../../assets/images/images";
import { tourType } from "../../../../../common/constants/tourProgramFilters";

interface TourProgramTableViewProps {
  month: number;
  year: number;
  data: MonthlyTourProgramData[];
  holiday: MonthlyHolidayData[];
  editable: boolean;
  specialAccessDays: string[];
  filterType?:
    | "All"
    | "Approved"
    | "Rejected"
    | "Approval Pending"
    | "Tour Not Set";
  refreshData?: () => void;
}

type SmartFillFlatEntry = {
  type: string;
  working_with_id: number | null;
  hq_brick_id: number | null;
  hq_territory_id: number | null;
  hq_headquarter_id: number | null;
};

// ✅ Represents all date-wise suggestions (key = date string)
type SmartFillSuggestions = {
  [date: string]: SmartFillFlatEntry[];
};

type SmartFillSuggestionsData = {
  success: boolean;
  dominant_month: number;
  suggestions: SmartFillSuggestions;
};

const TourProgramTableView = ({
  month,
  year,
  data,
  holiday = [],
  editable,
  specialAccessDays,
  filterType,
  refreshData,
}: TourProgramTableViewProps) => {
  const daysInMonth = moment(`${year}-${month}`, "YYYY-MM").daysInMonth();
  const { userDetails, decodeToken, userRole } = CustomerDatabaseStore();
  const { deleteTourProgram, addSmartTourProgram } = MonthlyTourProgramStore();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);

  // Smart Fill state
  const [isSmartFillOpen, setIsSmartFillOpen] = useState<boolean>(false);
  const [smartFillChoice, setSmartFillChoice] = useState<"Yes" | "No" | null>(
    null
  );
  // inside component
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [smartTourData, setSmartTourData] = useState<SmartFillSuggestions>({});
  const [isSmartTourReportingEditable, setIsSmartTourReportingEditable] =
    useState<boolean>(true);
  const [smartholidayData, setSmartHolidayData] = useState<
    MonthlySmartFillHolidayData[]
  >([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSmartFillRow, setShowSmartFillRow] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [existingToursForDate, setExistingToursForDate] = useState<
    MonthlyTourProgramData[]
  >([]);

  useEffect(() => {
    decodeToken();
  }, []);

  const fetchMonthlySmartFillData = async () => {
    setLoading(true);
    try {
      const payload = { month, year };
      const response = await addSmartTourProgram(payload);

      const suggestionsDateWiseData: SmartFillSuggestions | undefined =
        response?.suggestions?.suggestions;

      if (suggestionsDateWiseData) {
        setSmartTourData(suggestionsDateWiseData);
        setIsSmartFillOpen(true); // ✅ open modal when data arrives
      }
    } catch (error) {
      console.error("Error fetching Smart Fill data:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleSmartFillChoice = async (choice: "Yes" | "No") => {
    setSmartFillChoice(choice);
    if (choice === "Yes") {
      await fetchMonthlySmartFillData(); // ✅ call API only when Yes clicked
    } else {
      setShowSmartFillRow(false);
      setIsSmartFillOpen(false);
      setSmartTourData({}); // ✅ ensure modal stays closed if No
    }
  };

  // When closing the modal, reset the choice if needed
  const handleSmartFillClose = () => {
    setIsSmartFillOpen(false);
    setSmartFillChoice(null); // reset selection
  };

  const fullMonthData = Array.from({ length: daysInMonth }, (_, i) => {
    const date = moment(`${year}-${month}-${i + 1}`, "YYYY-MM-DD").format(
      "YYYY-MM-DD"
    );
    const isSunday = moment(date).day() === 0;
    const apiRow = data.filter((d) => d.date === date); // all entries for this date
    const holidayEntry = holiday.find((h) => h.date === date);

    return {
      date,
      isSunday,
      rowData: apiRow, 
      holiday: holidayEntry || null,
    };
  });

  const groupedByWeek: Record<string, typeof fullMonthData> = {};
  fullMonthData.forEach((item) => {
    const weekNum =
      moment(item.date).week() - moment(`${year}-${month}-01`).week() + 1;
    const weekKey = `Week ${weekNum}`;
    if (!groupedByWeek[weekKey]) groupedByWeek[weekKey] = [];
    groupedByWeek[weekKey].push(item);
  });

  const weekKeys = Object.keys(groupedByWeek);

  const renderStatusChip = (status: string | null) => {
    const commonStyle = {
      color: "#212121",
      fontWeight: 400,
      height: 20,
      fontSize: "0.7rem",
      borderRadius: "9px",
      px: 0.75,
    };
    switch (status) {
      case "active":
        return (
          <Chip
            label="Approved"
            size="small"
            sx={{ backgroundColor: "#6FC276", ...commonStyle }}
          />
        );
      case "inactive":
        return (
          <Chip
            label="Rejected"
            size="small"
            sx={{ backgroundColor: "#F08080", ...commonStyle }}
          />
        );
      case "pending":
        return (
          <Chip
            label="Approval Pending"
            size="small"
            sx={{ backgroundColor: "#FFE37A", ...commonStyle }}
          />
        );
      case null:
        return (
          <Chip
            label="Tour Not Set"
            size="small"
            sx={{ backgroundColor: "#FF964F", ...commonStyle }}
          />
        );
      default:
        return <Chip label={status} size="small" sx={commonStyle} />;
    }
  };

  const meetingDates = data
    .filter((d) => d.type === "Meeting" && d.status === "active")
    .map((d) => d.date);
  const renderActions = (
    rowData: MonthlyTourProgramData | null,
    isHoliday: boolean,
    date: string
  ) => {
    if (isHoliday) return "-";

    const isSpecialAccess = specialAccessDays.includes(
      moment(date).format("YYYY-MM-DD")
    );
    const isActionDisabled = !editable && !isSpecialAccess;

    // 🟡 Find all entries for this date
    const dateEntries = data.filter((d) => d.date === date);

    // 🟢 Allow multiple only for Field Work
    const hasNonFieldWork = dateEntries.some((d) => d.type !== "Field Work");
    const disableAdd =
      isActionDisabled || (hasNonFieldWork && rowData?.type !== "Field Work");

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={1}
        minWidth={48}
      >
        <IconButton
          size="small"
          sx={{
            color: disableAdd ? "grey.400" : "#1976d2",
            "&:hover": { color: disableAdd ? "grey.400" : "#115293" },
          }}
          onClick={() => handleAddClick(date, rowData)}
          disabled={disableAdd}
        >
          <AddCircleOutline fontSize="small" />
        </IconButton>

        {rowData?.id ? (
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteClick(rowData.id)}
            disabled={isActionDisabled}
          >
            <Delete fontSize="small" />
          </IconButton>
        ) : (
          <Box width={24} />
        )}
      </Box>
    );
  };

  const handleAddClick = (
    date: string,
    rowData: MonthlyTourProgramData | null
  ) => {
    const dateEntries = data.filter((d) => d.date === date);

    // ❌ If non-Field Work already exists for this date, block adding
    const hasNonFieldWork = dateEntries.some((d) => d.type !== "Field Work");

    if (hasNonFieldWork && (!rowData || rowData.type !== "Field Work")) {
      setSnackbar({
        open: true,
        message: "Only Field Work allows multiple entries for the same date.",
      });
      return;
    }

    if (rowData?.type === "Meeting") {
      setSnackbar({
        open: true,
        message: "Meeting already added for this day. Cannot add Field Work.",
      });
      return;
    }

    if (rowData?.type === "Meeting" && rowData.status === "active") {
      setSnackbar({
        open: true,
        message: "Approved Meeting cannot be modified.",
      });
      return;
    }
    setExistingToursForDate(dateEntries); // ✅ store for modal
    setSelectedDate(date);
    setModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDeleteId) return;
    if (isDeleting) return; // prevent double-click

    setIsDeleting(true);
    try {
      await deleteTourProgram(selectedDeleteId);
      setDeleteModalOpen(false);
      setSelectedDeleteId(null);
      refreshData?.(); // ✅ refresh the table after delete
    } catch (err) {
      console.error("Error deleting tour program:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  useEffect(() => {
    setShowSmartFillRow(true);
    setSmartFillChoice(null);
  }, [month, year]);

  const handleSave = async (payload: any) => {
    try {
      await refreshData?.(); // ✅ refresh table after adding entry
    } catch (error) {
      console.error("Error refreshing after save:", error);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          mt: 1,
          mb: 3,
        }}
      >
        <TableContainer
          component={Paper}
          sx={{ width: "100%", borderRadius: 2, boxShadow: 3 }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">Day</TableCell>
                <TableCell align="center">Type</TableCell>
                <TableCell align="center">Working With</TableCell>
                <TableCell align="center">Territory</TableCell>
                <TableCell align="center">Brick</TableCell>
                <TableCell align="center">Status</TableCell>
                {(editable || specialAccessDays.length > 0) && (
                  <TableCell align="center">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {/* --- Smart Fill Row before Week 1 --- */}
              {editable &&
                showSmartFillRow &&
                weekKeys.length > 0 &&
                data.length <= 5 && (
                  <TableRow sx={{ backgroundColor: "#ffffff !important" }}>
                    <TableCell
                      colSpan={editable ? 7 : 6}
                      sx={{
                        textAlign: "left",
                        backgroundColor: "#ffffff !important",
                        py: 0.5,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <img
                          src={IMAGES.SmartFill}
                          alt="Smart Fill"
                          style={{
                            width: 28,
                            height: 24,
                            objectFit: "contain",
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          fontWeight={550}
                          sx={{ color: "#226E9E" }}
                        >
                          Would you like to use Smart Fill?
                        </Typography>
                        {["Yes", "No"].map((choice) => (
                          <Button
                            key={choice}
                            variant={
                              smartFillChoice === choice
                                ? "contained"
                                : "outlined"
                            }
                            onClick={() =>
                              handleSmartFillChoice(choice as "Yes" | "No")
                            }
                            sx={{
                              color: "#226E9E",
                              borderRadius: "16px",
                              textTransform: "none",
                              minWidth: "50px",
                              paddingY: 0.1,
                              paddingX: 1.2,
                            }}
                          >
                            {choice}
                          </Button>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

              {weekKeys.map((week) => {
                const weekData = groupedByWeek[week];

                // Filter days for this week according to Quick Filter
                const filteredWeekData = weekData
                  .map((item) => {
                    const rows = item.rowData.filter((row) => {
                      if (!row)
                        return (
                          filterType === "All" || filterType === "Tour Not Set"
                        ); // null rows only for All / Tour Not Set
                      switch (filterType) {
                        case "All":
                          return true;
                        case "Approved":
                          return row.status === "active";
                        case "Rejected":
                          return row.status === "inactive";
                        case "Approval Pending":
                          return row.status === "pending";
                        case "Tour Not Set":
                          return row.status === null;
                        default:
                          return true;
                      }
                    });
                    // Only include this day if there are rows to display
                    return rows.length > 0 ||
                      filterType === "All" ||
                      filterType === "Tour Not Set"
                      ? { ...item, rowData: rows.length > 0 ? rows : [null] }
                      : null;
                  })
                  .filter(Boolean); // remove nulls

                // Skip this week entirely if no days are left
                if (filteredWeekData.length === 0) return null;

                return (
                  <React.Fragment key={week}>
                    {/* Week Header */}
                    <TableRow>
                      <TableCell
                        colSpan={
                          editable || specialAccessDays.length > 0
                            ? 7
                            : 6 
                        }
                        sx={{
                          fontWeight: 600,
                          backgroundColor:
                            editable || specialAccessDays.length > 0
                              ? "#f5f5f5"
                              : "#ffffff", 
                          textAlign: "center",
                          py: 0.2,
                        }}
                      >
                        {week}
                      </TableCell>
                    </TableRow>

                    {/* Days */}
                    {filteredWeekData.map((item, i) => {
                      if (!item) return null;
                      return item.rowData.map((row, idx) => (
                        <TableRow
                          key={`${i}-${idx}`}
                          sx={{
                            color: "#226E9E",
                            "& td": { py: 0.2, color: "#226E9E" },
                          }}
                        >
                          <TableCell align="center">
                            {moment(item.date).format("DD MMM")}
                            {item.isSunday || item.holiday ? (
                              <Typography
                                variant="caption"
                                display="block"
                                color={
                                  item.holiday ? "error.main" : "primary.main"
                                }
                              >
                                {item.holiday?.name || "Sunday"}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="center">
                            {row?.type || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {row?.working_with?.full_name || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {row?.hq_territory?.name || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {row?.hq_brick?.name || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {renderStatusChip(row?.status || null)}
                          </TableCell>
                          {(editable || specialAccessDays.length > 0) && (
                            <TableCell align="center">
                              {row ? (
                                renderActions(row, !!item.holiday, item.date)
                              ) : (
                                <Box
                                  display="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                  sx={{ ml: -4.5 }}
                                >
                                  <IconButton
                                    size="small"
                                    sx={{
                                      color: "#1976d2",
                                      "&:hover": { color: "#115293" },
                                    }}
                                    onClick={() =>
                                      handleAddClick(
                                        item.date,
                                        item.rowData[0] || null
                                      )
                                    }
                                    disabled={
                                      !editable &&
                                      !specialAccessDays.includes(
                                        moment(item.date).format("YYYY-MM-DD")
                                      )
                                    } // ⛔ Disable Add if not editable and not a special day
                                  >
                                    <AddCircleOutline fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ));
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>

          {data.length === 0 && (
            <Typography mt={2} textAlign="center" color="text.secondary">
              No records found for this filter.
            </Typography>
          )}
        </TableContainer>
      </Box>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        open={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting} // ✅ pass down
      />

      <AddTourProgramFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        tourTypes={tourType}
        defaultDate={selectedDate}
        meetingDates={meetingDates} // ✅ new prop
        existingTours={existingToursForDate} // ✅ new prop
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(255, 255, 255, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      <SmartFillModal
        open={isSmartFillOpen}
        onClose={handleSmartFillClose}
        data={smartTourData}
        loading={loading} // replace with actual loading state
        refreshData={refreshData} // ✅ pass refresh function
        closeParentModal={() => setIsSmartFillOpen(false)} // ✅ optional helper
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: "" })}
          severity="warning"
          sx={{
            width: "100%",
            backgroundColor: "#b64a4aff",
            color: "#ffffffff",
            "& .MuiAlert-icon": {
              color: "#ffffff", // 👈 Change icon color here
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TourProgramTableView;
